import { useEffect, useMemo, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  Users, UserCheck, PauseCircle, Sparkles, DollarSign, Repeat, CalendarDays, TrendingUp,
  Layers, MapPin, Loader2, Flag, ArrowUpDown, AlertTriangle, Building2, Handshake, Globe2,
  FileDown, Printer, Camera, ArrowDownRight,
} from "lucide-react";
import CoverageMap from "./directory/CoverageMap";
import { US_STATES } from "./directory/states";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadCsv, printReport } from "./reportExport";
import PartnerScorecard from "./directory/PartnerScorecard";

const CODE_TO_NAME = Object.fromEntries(US_STATES.map(([c, n]) => [c, n]));
const money = (v) => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function Kpi({ icon: Icon, label, value, sub, color = "text-primary" }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-9 h-9 rounded-xl bg-secondary flex items-center justify-center ${color}`}><Icon className="w-4.5 h-4.5" /></div>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="text-2xl font-heading font-extrabold leading-none">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, icon: Icon, children, right }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          <h3 className="font-heading font-bold text-sm">{title}</h3>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Trend({ title, data, color }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="text-xs text-muted-foreground mb-2">{title}</div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 4, right: 6, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`g-${title}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={1} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={40} />
          <Tooltip contentStyle={{ background: "#0d1420", border: "1px solid #1f2937", borderRadius: 8, fontSize: 12 }} />
          <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill={`url(#g-${title})`} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SortableTable({ columns, rows, initial, testid, onRowClick }) {
  const [sort, setSort] = useState(initial || { key: columns[0].key, dir: "desc" });
  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
  }, [rows, sort]);
  const toggle = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6 text-center">No data yet.</p>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid={testid}>
        <TableHeader><TableRow>
          {columns.map((c) => (
            <TableHead key={c.key}>
              <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggle(c.key)}>
                {c.label}<ArrowUpDown className="w-3 h-3 opacity-60" />
              </button>
            </TableHead>
          ))}
        </TableRow></TableHeader>
        <TableBody>
          {sorted.map((r, i) => (
            <TableRow key={r.id || r.zone_id || r.shop_id || i} className={onRowClick ? "cursor-pointer" : ""} onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => <TableCell key={c.key} className={c.className}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function MetricsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const [mapMetric, setMapMetric] = useState("revenue");
  const [growth, setGrowth] = useState(null);
  const [scorecardShop, setScorecardShop] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = stateFilter ? { state: stateFilter } : {};
      const [dash, gr] = await Promise.all([
        api.get("/admin/metrics/dashboard", { params }),
        api.get("/admin/metrics/growth"),
      ]);
      setData(dash.data); setGrowth(gr.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [stateFilter]);
  useEffect(() => { load(); }, [load]);

  const captureSnapshot = async () => {
    try { await api.post("/admin/metrics/snapshot"); toast.success("Snapshot captured"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const mapStates = useMemo(
    () => (data?.map_states || []).map((s) => ({ ...s, name: CODE_TO_NAME[s.state] || s.state })),
    [data]
  );
  const filterStates = useMemo(
    () => (data?.map_states || []).map((s) => s.state).sort((a, b) => (CODE_TO_NAME[a] || a).localeCompare(CODE_TO_NAME[b] || b)),
    [data]
  );

  if (loading || !data) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const a = data.advertisers, r = data.revenue, c = data.coverage, nc = data.national_coverage, at = data.attribution;

  const exportCsv = () => {
    const rows = [
      ["Velopaths Monthly Business Report"], ["Generated", new Date().toLocaleString()],
      ["Scope", stateFilter ? (CODE_TO_NAME[stateFilter] || stateFilter) : "National"], [],
      ["KPI", "Value"],
      ["National Coverage %", nc.pct], ["States Covered", `${nc.covered} / ${nc.total}`],
      ["Total Advertisers", a.total], ["Active", a.active], ["Paused", a.paused], ["New This Month", a.new_this_month],
      ["This Month Revenue", r.this_month], ["Last Month Revenue", r.last_month], ["YTD Revenue", r.ytd], ["MRR", r.mrr], ["ARR", r.arr],
      ["Active Zones", c.active_zones], ["Leased Zones", c.leased_zones], [],
      ["Revenue Attribution", "Amount"],
      ["Velopaths — Direct advertisers", at.velopaths.direct_advertisers], ["Velopaths — Zone lease income", at.velopaths.zone_lease_income],
      ["Velopaths — Share of ads", at.velopaths.velopaths_share], ["Velopaths — Total", at.velopaths.total],
      ["Partner — Share earned", at.partner.partner_share], ["Partner — Acquired ad revenue", at.partner.partner_acquired_revenue], [],
      ["Top States", "Shops", "Partners", "Advertisers", "Revenue"],
      ...mapStates.map((s) => [s.name, s.shops, s.partners, s.advertisers, s.revenue]), [],
      ["Top Shops", "Zones", "Advertisers", "Revenue", "Share"],
      ...data.top_shops.map((s) => [s.name, s.zones_leased, s.advertisers, s.revenue_generated, s.revenue_share_earned]), [],
      ["Top Zones", "State", "Advertisers", "Revenue", "Partner"],
      ...data.top_zones.map((z) => [z.name, z.state, z.advertiser_count, z.revenue, z.active_partner || ""]),
    ];
    downloadCsv("velopaths_monthly_report.csv", rows);
  };
  const exportPdf = () => {
    const row = (cells) => `<tr>${cells.map((x) => `<td>${x ?? ""}</td>`).join("")}</tr>`;
    printReport("Velopaths Monthly Report", `
      <h1>Velopaths Monthly Business Report</h1>
      <div class="muted">${new Date().toLocaleString()} · ${stateFilter ? (CODE_TO_NAME[stateFilter] || stateFilter) : "National"}</div>
      <div class="hero"><b style="font-size:26px">${nc.pct}% National Coverage</b><div class="muted">${nc.covered} / ${nc.total} states with bike shops have an active partner</div></div>
      <h2>Key metrics</h2><div class="kpis">
        <div class="kpi"><b>${a.total}</b><span>Advertisers (${a.active} active)</span></div>
        <div class="kpi"><b>${money(r.mrr)}</b><span>MRR</span></div>
        <div class="kpi"><b>${money(r.arr)}</b><span>ARR</span></div>
        <div class="kpi"><b>${money(r.ytd)}</b><span>YTD revenue</span></div>
        <div class="kpi"><b>${c.leased_zones}</b><span>Leased zones</span></div>
      </div>
      <h2>Revenue attribution</h2><table>
        <tr><th>Velopaths</th><th></th></tr>
        ${row(["Direct advertisers", money(at.velopaths.direct_advertisers)])}${row(["Zone lease income", money(at.velopaths.zone_lease_income)])}${row(["Share of ads", money(at.velopaths.velopaths_share)])}${row(["<b>Total</b>", "<b>" + money(at.velopaths.total) + "</b>"])}
        <tr><th>Partner</th><th></th></tr>${row(["Share earned", money(at.partner.partner_share)])}${row(["Acquired ad revenue", money(at.partner.partner_acquired_revenue)])}
      </table>
      <h2>Top states</h2><table><tr><th>State</th><th>Shops</th><th>Partners</th><th>Advertisers</th><th>Revenue</th></tr>${mapStates.slice(0, 15).map((s) => row([s.name, s.shops, s.partners, s.advertisers, money(s.revenue)])).join("")}</table>
      <h2>Top shops</h2><table><tr><th>Shop</th><th>Zones</th><th>Advertisers</th><th>Revenue</th><th>Share</th></tr>${data.top_shops.map((s) => row([s.name, s.zones_leased, s.advertisers, money(s.revenue_generated), money(s.revenue_share_earned)])).join("")}</table>
      <h2>Top zones</h2><table><tr><th>Zone</th><th>State</th><th>Advertisers</th><th>Revenue</th><th>Partner</th></tr>${data.top_zones.map((z) => row([z.name, z.state, z.advertiser_count, money(z.revenue), z.active_partner || "—"])).join("")}</table>`);
  };

  return (
    <div className="space-y-6" data-testid="metrics-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold">Business Dashboard</h2>
          <p className="text-sm text-muted-foreground">Advertiser growth, revenue, coverage & partner performance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={captureSnapshot} data-testid="metrics-snapshot-btn"><Camera className="w-4 h-4 mr-2" />Capture snapshot</Button>
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="metrics-export-csv"><FileDown className="w-4 h-4 mr-2" />CSV</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="metrics-export-pdf"><Printer className="w-4 h-4 mr-2" />PDF report</Button>
          <Select value={stateFilter || "all"} onValueChange={(v) => setStateFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="metrics-state-filter"><SelectValue placeholder="National" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">National (all states)</SelectItem>
              {filterStates.map((s) => <SelectItem key={s} value={s}>{CODE_TO_NAME[s] || s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* National coverage hero */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-primary/10 to-transparent p-5 flex items-center gap-5" data-testid="kpi-national-coverage">
        <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center"><Globe2 className="w-7 h-7 text-primary" /></div>
        <div>
          <div className="text-3xl font-heading font-extrabold">{nc.pct}% Coverage</div>
          <div className="text-sm text-muted-foreground">{nc.covered} / {nc.total} states with bike shops have an active partner</div>
        </div>
      </div>

      {/* KPI cards */}
      <div>
        <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Advertisers</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={Users} label="Total Advertisers" value={a.total} />
          <Kpi icon={UserCheck} label="Active" value={a.active} color="text-emerald-400" />
          <Kpi icon={PauseCircle} label="Paused" value={a.paused} color="text-amber-400" />
          <Kpi icon={Sparkles} label="New This Month" value={a.new_this_month} color="text-cyan-400" />
        </div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Revenue</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={DollarSign} label="This Month" value={money(r.this_month)} color="text-emerald-400" />
          <Kpi icon={CalendarDays} label="Last Month" value={money(r.last_month)} />
          <Kpi icon={TrendingUp} label="YTD Revenue" value={money(r.ytd)} color="text-cyan-400" />
          <Kpi icon={Repeat} label="MRR" value={money(r.mrr)} sub="Recurring monthly" color="text-violet-400" />
          <Kpi icon={TrendingUp} label="ARR" value={money(r.arr)} sub="MRR × 12" color="text-emerald-400" />
        </div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Coverage</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi icon={MapPin} label="Advertisers / State" value={c.advertisers_per_state} sub={`${c.states_covered} states covered`} />
          <Kpi icon={Layers} label="Advertisers / Zone" value={c.advertisers_per_zone} sub={`${c.zones_covered} zones covered`} />
          <Kpi icon={Layers} label="Active Zones" value={c.active_zones} color="text-cyan-400" />
          <Kpi icon={Flag} label="Leased Zones" value={c.leased_zones} color="text-emerald-400" />
        </div>
      </div>

      {growth?.current && (
        <Section title="Growth (from monthly snapshots)" icon={TrendingUp} right={<span className="text-xs text-muted-foreground">{growth.count} snapshot{growth.count === 1 ? "" : "s"}</span>}>
          <div className="grid sm:grid-cols-3 gap-4">
            {[["mom", "Month over month"], ["qoq", "Quarter over quarter"], ["yoy", "Year over year"]].map(([k, label]) => (
              <div key={k} className="rounded-xl border border-border p-3" data-testid={`growth-${k}`}>
                <div className="text-xs text-muted-foreground mb-2">{label}</div>
                {["revenue", "advertisers", "active_partners"].map((f) => {
                  const pct = growth[k]?.[f]?.pct;
                  return (
                    <div key={f} className="flex justify-between text-sm py-0.5">
                      <span className="capitalize text-muted-foreground">{f.replace("_", " ")}</span>
                      <span className={pct == null ? "text-muted-foreground" : pct >= 0 ? "text-emerald-400" : "text-rose-400"}>{pct == null ? "—" : `${pct >= 0 ? "+" : ""}${pct}%`}</span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Revenue attribution */}
      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Velopaths Revenue" icon={DollarSign}>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between"><span className="text-muted-foreground">Direct advertisers</span><b>{money(at.velopaths.direct_advertisers)}</b></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Zone lease income</span><b>{money(at.velopaths.zone_lease_income)}</b></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Velopaths share of ads</span><b>{money(at.velopaths.velopaths_share)}</b></li>
            <li className="flex justify-between border-t border-border pt-2"><span>Total Velopaths</span><b className="text-emerald-400">{money(at.velopaths.total)}</b></li>
          </ul>
        </Section>
        <Section title="Partner Revenue" icon={Handshake}>
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between"><span className="text-muted-foreground">Partner share earned</span><b>{money(at.partner.partner_share)}</b></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Partner-acquired ad revenue</span><b>{money(at.partner.partner_acquired_revenue)}</b></li>
            <li className="flex justify-between"><span className="text-muted-foreground">Partner-acquired advertisers</span><b>{at.partner.partner_acquired_advertisers}</b></li>
          </ul>
        </Section>
      </div>

      {/* Geographic revenue map */}
      <Section title="Geographic revenue map" icon={MapPin} right={
        <div className="flex rounded-lg border border-border overflow-hidden text-xs">
          {[["shops", "Shops"], ["partners", "Partners"], ["revenue", "Revenue"]].map(([k, l]) => (
            <button key={k} onClick={() => setMapMetric(k)} className={`px-2.5 py-1 ${mapMetric === k ? "bg-primary/15 text-primary" : "text-muted-foreground"}`} data-testid={`map-metric-${k}`}>{l}</button>
          ))}
        </div>
      }>
        <CoverageMap states={mapStates} metric={mapMetric} selected={stateFilter} onSelect={(code) => code && setStateFilter(stateFilter === code ? "" : code)} />
      </Section>

      {/* Trend charts */}
      <Section title="12-month trends" icon={TrendingUp}>
        <div className="grid md:grid-cols-2 gap-4">
          <Trend title="Revenue" data={data.trends.revenue} color="#34d399" />
          <Trend title="Advertiser growth" data={data.trends.advertisers} color="#22d3ee" />
          <Trend title="Partner growth" data={data.trends.partners} color="#a78bfa" />
          <Trend title="Zone lease growth" data={data.trends.leases} color="#fbbf24" />
        </div>
      </Section>

      {/* Top performance */}
      <Section title="Top zones" icon={Layers}>
        <SortableTable testid="top-zones-table" initial={{ key: "revenue", dir: "desc" }} rows={data.top_zones}
          columns={[
            { key: "name", label: "Zone" },
            { key: "state", label: "State" },
            { key: "advertiser_count", label: "Advertisers" },
            { key: "revenue", label: "Revenue", render: (r) => money(r.revenue) },
            { key: "active_partner", label: "Partner", render: (r) => r.active_partner || <span className="text-muted-foreground">—</span> },
          ]} />
      </Section>
      <Section title="Top shops (partners)" icon={Building2}>
        <SortableTable testid="top-shops-table" onRowClick={(row) => setScorecardShop(row.shop_id)} initial={{ key: "revenue_generated", dir: "desc" }} rows={data.top_shops}
          columns={[
            { key: "name", label: "Shop" },
            { key: "zones_leased", label: "Zones Leased" },
            { key: "advertisers", label: "Advertisers" },
            { key: "revenue_generated", label: "Revenue", render: (r) => money(r.revenue_generated) },
            { key: "revenue_share_earned", label: "Share Earned", render: (r) => money(r.revenue_share_earned) },
          ]} />
      </Section>
      <Section title="Top advertisers" icon={Users}>
        <SortableTable testid="top-advertisers-table" initial={{ key: "spend", dir: "desc" }} rows={data.top_advertisers}
          columns={[
            { key: "name", label: "Advertiser" },
            { key: "zone", label: "Zone", render: (r) => r.zone || <span className="text-muted-foreground">—</span> },
            { key: "spend", label: "Spend", render: (r) => money(r.spend) },
            { key: "start_date", label: "Start" },
            { key: "status", label: "Status", render: (r) => <Badge className="capitalize">{r.status}</Badge> },
          ]} />
      </Section>

      {data.alerts.revenue_alerts?.length > 0 && (
        <Section title="Declining revenue alerts" icon={ArrowDownRight}>
          <div className="overflow-x-auto">
            <Table data-testid="revenue-alerts-table">
              <TableHeader><TableRow>
                <TableHead>Partner</TableHead><TableHead>Previous</TableHead><TableHead>Current</TableHead>
                <TableHead>% Change</TableHead><TableHead>Level</TableHead><TableHead>Recommended action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.alerts.revenue_alerts.map((x) => {
                  const color = x.level === "Critical" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                    : x.level === "High Risk" ? "bg-orange-500/15 text-orange-300 border-orange-500/30"
                    : "bg-amber-500/15 text-amber-300 border-amber-500/30";
                  return (
                    <TableRow key={x.shop_id}>
                      <TableCell className="font-medium">{x.name}</TableCell>
                      <TableCell>{money(x.previous)}</TableCell>
                      <TableCell>{money(x.current)}</TableCell>
                      <TableCell className="text-rose-400">{x.pct_change}%</TableCell>
                      <TableCell><Badge className={`${color} border`}>{x.level}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{x.recommended_action}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Section>
      )}

      {/* Alerts */}
      <div className="grid md:grid-cols-3 gap-4">
        <Section title="Expiring advertisers" icon={AlertTriangle}>
          {data.alerts.expiring_advertisers.length === 0 ? <p className="text-xs text-muted-foreground">None expiring within 30 days.</p> :
            <ul className="space-y-1.5 text-sm">{data.alerts.expiring_advertisers.map((x) => (
              <li key={x.id} className="flex justify-between"><span className="truncate">{x.name}</span><span className="text-amber-400 text-xs">{x.expires_in_days}d</span></li>
            ))}</ul>}
        </Section>
        <Section title="Unleased premium zones" icon={Flag}>
          {data.alerts.unleased_premium_zones.length === 0 ? <p className="text-xs text-muted-foreground">No high-value zones available.</p> :
            <ul className="space-y-1.5 text-sm">{data.alerts.unleased_premium_zones.map((x) => (
              <li key={x.zone_id} className="flex justify-between"><span className="truncate">{x.name} <span className="text-muted-foreground">· {x.classification}</span></span><span className="text-cyan-400 text-xs">{money(x.annual_fee)}</span></li>
            ))}</ul>}
        </Section>
        <Section title="Partner risk" icon={AlertTriangle}>
          {data.alerts.partner_risk.length === 0 ? <p className="text-xs text-muted-foreground">No at-risk partners.</p> :
            <ul className="space-y-1.5 text-sm">{data.alerts.partner_risk.map((x) => (
              <li key={x.shop_id} className="flex justify-between"><span className="truncate">{x.name}</span><span className="text-rose-400 text-xs">{x.reason}</span></li>
            ))}</ul>}
        </Section>
      </div>

      <PartnerScorecard shopId={scorecardShop} open={!!scorecardShop} onOpenChange={(v) => !v && setScorecardShop(null)} />
    </div>
  );
}
